import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Truck, Droplets, Flower2, Award } from "lucide-react";

const BG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_premium-suds-2/artifacts/f98bmn4i_IMG-20260817-WA0017.jpg";

const features = [
  { icon: Truck, label: "Transport gratuit" },
  { icon: Droplets, label: "Esență 100% originală" },
  { icon: Flower2, label: "Parfum puternic" },
  { icon: Award, label: "Calitate Premium" },
];

const parent = { hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } } };
const child = { hidden: { y: "115%" }, show: { y: "0%", transition: { duration: 0.85, ease: [0.19, 1, 0.22, 1] } } };
const Line = ({ children, className = "" }) => (
  <span className="reveal-line py-[0.04em]">
    <motion.span variants={child} className={`block pb-[0.08em] ${className}`}>{children}</motion.span>
  </span>
);

export default function Hero({ onExplore }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section
      ref={ref}
      className="relative h-screen min-h-[640px] w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full-bleed background image (bottles on the right) */}
      <motion.div style={{ scale: imgScale, y: imgY }} className="absolute inset-0">
        <img src={BG} alt="Detergenți lichizi concentrați premium" className="w-full h-full object-cover object-right" />
      </motion.div>

      {/* Legibility gradients: keep left clean for text, blend bottom into marquee */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#EDEAE3] via-[#EDEAE3]/85 to-transparent md:to-transparent" />
      <div className="absolute inset-0 md:hidden bg-[#EDEAE3]/40" />
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-[#EDEAE3]" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex items-center">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="text-[11px] md:text-xs tracking-[0.5em] uppercase text-[#4A4844] mb-6"
          >
            Detergenți lichizi concentrați
          </motion.p>

          <motion.h1
            variants={parent}
            initial="hidden"
            animate="show"
            className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0E0F12]"
          >
            <Line>Curățenie profesională,</Line>
            <Line className="italic text-[#4A4844]">rezultate impecabile.</Line>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-3"
            data-testid="hero-features"
          >
            {features.map((f) => (
              <span
                key={f.label}
                className="inline-flex items-center gap-2.5 rounded-full border border-black/12 bg-[#EDEAE3]/70 backdrop-blur-sm px-4 py-2.5 text-[13px] tracking-wide text-[#0E0F12]"
              >
                <f.icon className="w-4 h-4 text-gramur" strokeWidth={1.6} />
                {f.label}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-11"
          >
            <button
              data-testid="hero-explore-button"
              onClick={onExplore}
              className="group inline-flex items-center gap-3 px-10 py-4 bg-[#0E0F12] text-[#EDEAE3] text-sm tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform duration-300"
            >
              Descoperă produsele
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
